import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import { Button } from "./Button";
import type { StatusTone } from "./model";
import { dsFont, toneBg } from "./styles";

export type AttentionRowProps = {
  name: string;
  meta: string;
  reason: string;
  tone: StatusTone;
  /** Shows the translated "example" tag. Required for sample rows. */
  example?: boolean;
  actionLabel: string;
  onAction?: () => void;
};

/** Part name, id, and status stacked, with the review pill on the right. */
export function AttentionRow({
  name,
  meta,
  reason,
  tone,
  example = false,
  actionLabel,
  onAction,
}: AttentionRowProps) {
  const t = useT();
  return (
    <div
      data-ds=""
      data-testid="ds-attention"
      className={cx(
        dsFont,
        "grid min-h-ds-row-attention grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-1",
      )}
    >
      <div className="min-w-0">
        <b className="block whitespace-nowrap text-ds-item font-semibold text-ds-ink">
          {name}
          {example ? (
            <span className="ml-1.5 inline-block rounded-ds-pill border border-solid border-ds-hair px-1.5 py-px align-middle text-ds-badge-sm font-normal text-ds-ink-3">
              {t("ds.attention.example")}
            </span>
          ) : null}
        </b>
        <small className="mt-0.5 block whitespace-nowrap text-ds-label font-normal text-ds-ink-3">
          {meta}
        </small>
        <span className="mt-0.5 inline-flex items-center gap-[7px] whitespace-nowrap text-ds-meta font-normal text-ds-ink-2">
          <span className={cx("size-[6px] shrink-0 rounded-full", toneBg(tone))} aria-hidden />
          {reason}
        </span>
      </div>
      <div>
        <Button variant="secondary" onClick={onAction}>
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

/** Threshold sentence shown when nothing needs a review. */
export function AttentionEmpty() {
  const t = useT();
  return (
    <div data-ds="" data-testid="ds-attention-empty" className={cx(dsFont, "px-1 py-3.5 text-center")}>
      <b className="mb-1 block text-ds-item font-semibold text-ds-ink">{t("ds.attention.emptyTitle")}</b>
      <p className="m-0 text-ds-item font-normal text-ds-ink-3">{t("ds.attention.emptyBody")}</p>
    </div>
  );
}
